import { atom, type Atom, type PrimitiveAtom, type WritableAtom } from 'jotai';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports -- Jotai RESET is a `unique symbol`; atom generics need `typeof RESET`, which requires a value binding
import { atomWithReset, RESET } from 'jotai/utils';

import { getDocument } from '@atlaskit/browser-apis';

import type { TenantMode } from '../pages/labs/home/ConfigurationPanel/TenantForm';
import { DEFAULT_TENANT_BASE_URL } from '../pages/labs/home/constants';
import type { CypherLspClient } from '../pages/labs/lsp/cypherClient';

export const QueryBuilderSelectedEditorTabAtom: PrimitiveAtom<number> & {
	init: number;
} = atom<number>(0);

// Base atoms for individual state values
export const QueryBuilderTenantUrlAtom: PrimitiveAtom<string> & {
	init: string;
} = atom<string>('');
export const QueryBuilderTenantModeAtom: PrimitiveAtom<TenantMode> & {
	init: TenantMode;
} = atom<TenantMode>('default');
export const QueryBuilderSelectedSiteCloudIdAtom: PrimitiveAtom<string> & {
	init: string;
} = atom<string>('');
export const QueryBuilderCypherQueryAtom: PrimitiveAtom<string> & {
	init: string;
} = atom<string>('');
export const QueryBuilderGraphQLQueryAtom: PrimitiveAtom<string> & {
	init: string;
} = atom<string>('');
export const QueryBuilderUserStaffIdAtom: PrimitiveAtom<string | null> & {
	init: string | null;
} = atom<string | null>(null);

// Parameter rows state
export type ParameterRowType = 'parameter' | 'header';

export type ParameterRowState = {
	id: string;
	type: ParameterRowType | null;
	name: string;
	value: string;
	enabled: boolean;
};

const randomRowId = (prefix: string) => `${prefix}_${Math.random().toString(36).slice(2, 10)}`;

const createParameterRowState = (overrides?: Partial<ParameterRowState>): ParameterRowState => ({
	id: overrides?.id ?? randomRowId('row'),
	type: overrides?.type ?? null,
	name: overrides?.name ?? '',
	value: overrides?.value ?? '',
	enabled: overrides?.enabled ?? true,
});

export const queryBuilderParameterRowIdsBaseAtom: PrimitiveAtom<string[]> & {
	init: string[];
} = atom<string[]>([]);
export const queryBuilderParameterRowsBaseAtom: PrimitiveAtom<Record<string, ParameterRowState>> & {
	init: Record<string, ParameterRowState>;
} = atom<Record<string, ParameterRowState>>({});

const rowsToHeadersMap = (rows: Record<string, ParameterRowState>): Record<string, string> => {
	const result: Record<string, string> = {};
	Object.values(rows).forEach((row) => {
		if (row.type !== 'header' || !row.enabled) {
			return;
		}
		const key = row.name.trim();
		if (key) {
			result[key] = row.value;
		}
	});
	return result;
};

const rowsToParamsMap = (rows: Record<string, ParameterRowState>): Record<string, unknown> => {
	const result: Record<string, unknown> = {};
	Object.values(rows).forEach((row) => {
		if (row.type !== 'parameter' || !row.enabled) {
			return;
		}
		const key = row.name.trim();
		if (key) {
			result[key] = row.value;
		}
	});
	return result;
};

const syncRowsForType = (
	get: any,
	set: any,
	type: ParameterRowType,
	entries: Record<string, unknown>,
) => {
	const rows = get(queryBuilderParameterRowsBaseAtom) as Record<string, ParameterRowState>;
	const ids = get(queryBuilderParameterRowIdsBaseAtom) as string[];

	const nextRows: Record<string, ParameterRowState> = {};
	const nextIds: string[] = [];

	ids.forEach((id) => {
		const row = rows[id];
		if (!row) {
			return;
		}
		if (row.type === type) {
			return;
		}
		nextRows[id] = row;
		nextIds.push(id);
	});

	Object.entries(entries).forEach(([rawName, rawValue]) => {
		const name = rawName.trim();
		if (!name) {
			return;
		}
		const value = rawValue == null ? '' : String(rawValue);
		let newRow = createParameterRowState({ type, name, value });
		while (nextRows[newRow.id]) {
			newRow = createParameterRowState({ type, name, value });
		}
		nextRows[newRow.id] = newRow;
		nextIds.push(newRow.id);
	});

	set(queryBuilderParameterRowsBaseAtom, nextRows);
	set(queryBuilderParameterRowIdsBaseAtom, nextIds);
};

export const queryBuilderParameterRowIdsAtom: Atom<string[]> = atom<string[]>((get) =>
	get(queryBuilderParameterRowIdsBaseAtom),
);
export const queryBuilderParameterRowsAtom: Atom<ParameterRowState[]> = atom<ParameterRowState[]>(
	(get) => {
		const ids = get(queryBuilderParameterRowIdsBaseAtom) as string[];
		const rows = get(queryBuilderParameterRowsBaseAtom) as Record<string, ParameterRowState>;
		return ids.map((id) => rows[id] ?? createParameterRowState({ id }));
	},
);

export type ParameterRowUpdater =
	| Partial<ParameterRowState>
	| ((current: ParameterRowState) => ParameterRowState);

export const queryBuilderUpdateParameterRowAtom: WritableAtom<
	null,
	[
		payload: {
			id: string;
			updater: ParameterRowUpdater;
		},
	],
	void
> & {
	init: null;
} = atom(null, (get, set, payload: { id: string; updater: ParameterRowUpdater }) => {
	const rows = get(queryBuilderParameterRowsBaseAtom) as Record<string, ParameterRowState>;
	const ids = get(queryBuilderParameterRowIdsBaseAtom) as string[];
	const { id, updater } = payload;
	const previous = rows[id] ?? createParameterRowState({ id });
	const nextBase =
		typeof updater === 'function'
			? (updater as (current: ParameterRowState) => ParameterRowState)(previous)
			: { ...previous, ...updater };
	const next: ParameterRowState = {
		id: previous.id,
		type: nextBase.type ?? null,
		name: nextBase.name ?? '',
		value: nextBase.value ?? '',
		enabled: nextBase.enabled ?? true,
	};
	set(queryBuilderParameterRowsBaseAtom, { ...rows, [id]: next });
	if (!rows[id]) {
		set(queryBuilderParameterRowIdsBaseAtom, [...ids, id]);
	}
});

export const queryBuilderAddParameterRowAtom: WritableAtom<
	null,
	[initial?: Partial<ParameterRowState> | undefined],
	void
> & {
	init: null;
} = atom(null, (get, set, initial?: Partial<ParameterRowState>) => {
	const rows = get(queryBuilderParameterRowsBaseAtom) as Record<string, ParameterRowState>;
	const ids = get(queryBuilderParameterRowIdsBaseAtom) as string[];
	let row = createParameterRowState(initial);
	while (rows[row.id]) {
		row = createParameterRowState({ ...initial, id: undefined });
	}
	set(queryBuilderParameterRowsBaseAtom, { ...rows, [row.id]: row });
	set(queryBuilderParameterRowIdsBaseAtom, [...ids, row.id]);
});

export const queryBuilderRemoveParameterRowAtom: WritableAtom<null, [id: string], void> & {
	init: null;
} = atom(null, (get, set, id: string) => {
	const rows = get(queryBuilderParameterRowsBaseAtom) as Record<string, ParameterRowState>;
	if (!rows[id]) {
		return;
	}
	const ids = get(queryBuilderParameterRowIdsBaseAtom) as string[];
	const { [id]: _removed, ...rest } = rows;
	set(queryBuilderParameterRowsBaseAtom, rest);
	set(
		queryBuilderParameterRowIdsBaseAtom,
		ids.filter((existingId) => existingId !== id),
	);
});

export const queryBuilderClearParameterRowsAtom: WritableAtom<null, [], void> & {
	init: null;
} = atom(null, (_get, set) => {
	set(queryBuilderParameterRowsBaseAtom, {});
	set(queryBuilderParameterRowIdsBaseAtom, []);
});

export const QueryBuilderHeadersAtom: WritableAtom<
	Record<string, string>,
	[update: unknown],
	void
> = atom(
	(get) =>
		rowsToHeadersMap(get(queryBuilderParameterRowsBaseAtom) as Record<string, ParameterRowState>),
	(get, set, update) => {
		const previous = rowsToHeadersMap(
			get(queryBuilderParameterRowsBaseAtom) as Record<string, ParameterRowState>,
		);
		const resolved =
			(typeof update === 'function'
				? (update as (prev: Record<string, string>) => Record<string, string>)(previous)
				: update) ?? {};
		syncRowsForType(get, set, 'header', resolved as Record<string, unknown>);
	},
);

export const QueryBuilderParamsAtom: WritableAtom<
	Record<string, unknown>,
	[update: unknown],
	void
> = atom(
	(get) =>
		rowsToParamsMap(get(queryBuilderParameterRowsBaseAtom) as Record<string, ParameterRowState>),
	(get, set, update) => {
		const previous = rowsToParamsMap(
			get(queryBuilderParameterRowsBaseAtom) as Record<string, ParameterRowState>,
		);
		const resolved =
			(typeof update === 'function'
				? (update as (prev: Record<string, unknown>) => Record<string, unknown>)(previous)
				: update) ?? {};
		syncRowsForType(get, set, 'parameter', resolved as Record<string, unknown>);
	},
);

// Query builder always uses live data.
export const QueryBuilderIsDebugEnabledAtom: Atom<boolean> = atom(() => false);
export const QueryBuilderForceConfettiAtom: PrimitiveAtom<boolean> & {
	init: boolean;
} = atom<boolean>(false);
export const QueryBuilderForceShowFlockAnalyticsAtom: PrimitiveAtom<boolean> & {
	init: boolean;
} = atom<boolean>(false);
export const QueryBuilderGraphQLLimitAtom: PrimitiveAtom<number> & {
	init: number;
} = atom<number>(1000);

export const QUERY_BUILDER_DEVELOPER_MODE_STORAGE_KEY = 'query-builder-developer-mode';
export const QUERY_BUILDER_LEFT_SIDEBAR_VISIBLE_STORAGE_KEY = 'query-builder-left-sidebar-visible';
export const QUERY_BUILDER_RESULTS_LAYOUT_STORAGE_KEY = 'query-builder-results-layout';
export const QUERY_BUILDER_RESULTS_VIEW_STORAGE_KEY = 'query-builder-results-view';

const initialQueryBuilderDeveloperMode = (() => {
	const doc = getDocument();
	const win = doc?.defaultView;
	if (!win) {
		return false;
	}
	try {
		return win.localStorage.getItem(QUERY_BUILDER_DEVELOPER_MODE_STORAGE_KEY) === 'true';
	} catch {
		return false;
	}
})();

export const QueryBuilderDeveloperModeAtom: PrimitiveAtom<boolean> & {
	init: boolean;
} = atom<boolean>(initialQueryBuilderDeveloperMode);

const initialQueryBuilderLeftSidebarVisible = (() => {
	const doc = getDocument();
	const win = doc?.defaultView;
	if (!win) {
		return true;
	}
	try {
		const stored = win.localStorage.getItem(QUERY_BUILDER_LEFT_SIDEBAR_VISIBLE_STORAGE_KEY);
		return stored === null ? true : stored === 'true';
	} catch {
		return true;
	}
})();

export const QueryBuilderLeftSidebarVisibleAtom: PrimitiveAtom<boolean> & {
	init: boolean;
} = atom<boolean>(initialQueryBuilderLeftSidebarVisible);

const initialQueryBuilderResultsVerticalLayout = (() => {
	const doc = getDocument();
	const win = doc?.defaultView;
	if (!win) {
		return false;
	}
	try {
		return win.localStorage.getItem(QUERY_BUILDER_RESULTS_LAYOUT_STORAGE_KEY) === 'vertical';
	} catch {
		return false;
	}
})();

export const QueryBuilderResultsVerticalLayoutAtom: PrimitiveAtom<boolean> & {
	init: boolean;
} = atom<boolean>(initialQueryBuilderResultsVerticalLayout);

type ResultsViewPreference = 'table' | 'json' | null;

const initialQueryBuilderResultsViewPreference: ResultsViewPreference = (() => {
	const doc = getDocument();
	const win = doc?.defaultView;
	if (!win) {
		return null;
	}
	try {
		const stored = win.localStorage.getItem(QUERY_BUILDER_RESULTS_VIEW_STORAGE_KEY);
		return stored === 'table' || stored === 'json' ? stored : null;
	} catch {
		return null;
	}
})();

export const QueryBuilderResultsViewPreferenceAtom: PrimitiveAtom<ResultsViewPreference> & {
	init: ResultsViewPreference;
} = atom<ResultsViewPreference>(initialQueryBuilderResultsViewPreference);

// Mock errors atom - stores array of error type keys that should be injected
export type MockErrorType = 'something-went-wrong' | 'null-value-non-nullable' | 'validation-error';
export const QueryBuilderMockErrorsAtom: PrimitiveAtom<MockErrorType[]> & {
	init: MockErrorType[];
} = atom<MockErrorType[]>([]);

export const QueryBuilderAggBaseUrlAtom: Atom<string> = atom(() => DEFAULT_TENANT_BASE_URL);

// Execution state atoms - using atomWithReset for atoms that clear on execute
export const QueryBuilderNetworkResultAtom: WritableAtom<any, [any], void> & {
	init: any;
} = atomWithReset<any>(undefined);
export const QueryBuilderRunningAtom: PrimitiveAtom<boolean> & {
	init: boolean;
} = atom<boolean>(false);
export const QueryBuilderSpotlightAtom: WritableAtom<
	'graphql' | 'cypher' | null,
	[
		| typeof RESET
		| 'graphql'
		| 'cypher'
		| ((prev: 'graphql' | 'cypher' | null) => typeof RESET | 'graphql' | 'cypher' | null)
		| null,
	],
	void
> & {
	init: 'graphql' | 'cypher' | null;
} = atomWithReset<'cypher' | 'graphql' | null>(null);
export const QueryBuilderRoundTripMsAtom: PrimitiveAtom<number | null> & {
	init: number | null;
} = atom<number | null>(null);
export const QueryBuilderAuthLoginUrlAtom: PrimitiveAtom<string | null> & {
	init: string | null;
} = atom<string | null>(null);

// Editor state atoms
export type CypherEditorPosition = {
	line: number;
	column: number;
};

export const QueryBuilderCypherEditorPositionAtom: PrimitiveAtom<CypherEditorPosition | null> & {
	init: CypherEditorPosition | null;
} = atom<CypherEditorPosition | null>(null);

export const QueryBuilderCypherLspClientAtom: PrimitiveAtom<CypherLspClient | null> & {
	init: CypherLspClient | null;
} = atom<CypherLspClient | null>(null);

export type ReturnLabelEntry = { name: string; labels: string[] };

export const QueryBuilderReturnLabelsAtom: WritableAtom<
	ReturnLabelEntry[] | null,
	[
		| typeof RESET
		| ReturnLabelEntry[]
		| ((prev: ReturnLabelEntry[] | null) => typeof RESET | ReturnLabelEntry[] | null)
		| null,
	],
	void
> & {
	init: ReturnLabelEntry[] | null;
} = atomWithReset<ReturnLabelEntry[] | null>(null);

// Atom to store semantic errors from query execution
export type SemanticError = {
	message: string;
	line?: number;
	column?: number;
};

export const QueryBuilderSemanticErrorsAtom: WritableAtom<
	SemanticError[],
	[typeof RESET | SemanticError[] | ((prev: SemanticError[]) => typeof RESET | SemanticError[])],
	void
> & {
	init: SemanticError[];
} = atomWithReset<SemanticError[]>([]);

// Atom to trigger formatting in the Cypher editor
export const QueryBuilderTriggerCypherFormatAtom: PrimitiveAtom<boolean> & {
	init: boolean;
} = atom<boolean>(false);

// Atom to store a proposed Cypher query from the AI assistant (for diff view)
export type ProposedCypherQuery = {
	query: string;
};
export const QueryBuilderProposedCypherQueryAtom: PrimitiveAtom<ProposedCypherQuery | null> & {
	init: ProposedCypherQuery | null;
} = atom<ProposedCypherQuery | null>(null);

// UGS schema loading error atom (nodes/relationships schema loading failed)
export const QueryBuilderUgsSchemaErrorAtom: PrimitiveAtom<string | null> & {
	init: string | null;
} = atom<string | null>(null);
